import { panelFor as panelAt } from '../components/Shell/panelRoutes';
import { PanelResultsContext } from './PanelResultsContext';
import { followPanel, panelUrl, startedBy } from '../components/Shell/panelFollow';
import { fetchModelMenu, FALLBACK_MENU, DEFAULT_MODEL } from '../services/modelMenu';
import { readSavedModel, openingModel, canUseModel, MODEL_SAVED } from '../services/modelChoice';
import { deepLeft, browserTimeZone } from '../services/meterFormat';
import { stripNext } from '../components/AntigravitySidebar/replyText';
import React, { createContext, useContext, useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { API_BASE } from '../apiSession';
import { invalidateResumes } from '../services/resumes';
import { factsChanged } from '../services/aboutYouEvents';
import { useConfirm } from './ConfirmContext';

const OmniAgentContext = createContext(null);

/**
 * Everything the chat holds, and every way to act on it. A component that
 * calls this renders again whenever ANY of it changes, which during a reply
 * is every word: fine for the chat itself, which shows the words, and wrong
 * for anything else. A page beside the chat reads what it needs with
 * useOmniAgentPick or useOmniAgentActions below.
 */
export const useOmniAgent = () => {
  const context = useContext(OmniAgentContext);
  if (!context) {
    throw new Error('useOmniAgent must be used within an OmniAgentProvider');
  }
  return context;
};

// ── Reading only what you show (2026-10-31) ─────────────────────────────────
//
// The founder: "extremely slow user feel as well in touch and type". The one
// context above changed with every streamed word, so every component that
// read it rendered again for every word: the navigation rail's 24 chats (a
// menu and a formatted date each), the Resume page, its every section and
// bullet list, the Applications page, About you. In a browser with the CPU
// at a phone's pace that was 4.6 s of script for a five-second reply
// (e2e/speed.mjs), and typing during a reply lagged a frame behind.
//
// The actions are now one object that never changes (they always call the
// latest code), and a component picks the values it shows by name; it
// renders again only when one of those changes. No new package: React 18's
// own useSyncExternalStore over a store the provider writes after each
// render.

// The action names, in one list: each is a stable function on the object
// useOmniAgentActions returns, and in useOmniAgent's value.
const ACTIONS = [
  'sendMessage', 'postCard', 'updateCard', 'removeCard', 'regenerate', 'toggleSidebar',
  'setActiveModel', 'setThinkHarder', 'refreshMenu', 'loadSession', 'startNewSession',
  'deleteSession', 'pinSession', 'renameSession', 'linkSessionWorkspace', 'setActiveEditContext',
  'acceptChange', 'acceptChanges', 'dismissChanges', 'rejectChange', 'rejectChanges',
  'acceptArrangement', 'rejectArrangement', 'stopGeneration', 'setIsSidebarOpen', 'choosePanel',
];

const OmniActionsContext = createContext(null);
const NO_MESSAGES = Object.freeze([]);
const OmniStoreContext = createContext(null);

/** Every action, as functions that never change: reading this never re-renders. */
export const useOmniAgentActions = () => {
  const actions = useContext(OmniActionsContext);
  if (!actions) throw new Error('useOmniAgentActions must be used within an OmniAgentProvider');
  return actions;
};

/**
 * The named values (and actions), e.g. useOmniAgentPick('sessions',
 * 'activeSessionId', 'loadSession'). Renders again only when one of the named
 * values changes, never for a streamed word unless 'messages' is named.
 */
export function useOmniAgentPick(...keys) {
  const store = useContext(OmniStoreContext);
  if (!store) throw new Error('useOmniAgentPick must be used within an OmniAgentProvider');
  const last = useRef(null);
  const snapshot = () => {
    const value = store.get();
    const prev = last.current;
    if (prev && keys.every(k => prev[k] === value[k])) return prev;
    const next = {};
    for (const k of keys) next[k] = value[k];
    last.current = next;
    return next;
  };
  return useSyncExternalStore(store.subscribe, snapshot, snapshot);
}

function makeStore() {
  let value = null;
  const listeners = new Set();
  return {
    get: () => value,
    set: (next) => { value = next; },
    emit: () => listeners.forEach(fn => fn()),
    subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
  };
}

// The applications each reply started, kept as the same Map until they
// change: it was rebuilt for every streamed word, and the panel beside the
// chat rendered again each time for an answer that had not changed.
function sameStarted(a, b) {
  if (a.size !== b.size) return false;
  for (const [id, jobs] of a) {
    const other = b.get(id);
    if (!other || other.length !== jobs.length || other.some((j, i) => j !== jobs[i])) return false;
  }
  return true;
}

// Reads a tool's result off the stream.
//
// LangChain does not hand back one consistent shape: sometimes the raw string
// the tool returned, sometimes a message object carrying it on `kwargs.content`
// or `content`. The resume-entry branch below already handled all three — the
// navigate and design branches only tried JSON.parse on the raw value, and
// their failure went into a catch that logged nothing useful.
//
// That is why "switch to a different template" said it had switched and
// nothing happened: the tool ran, the server streamed the result, and the
// browser threw the payload away before anything could apply it.
function parseToolOutput(output) {
  const raw = typeof output === 'string'
    ? output
    : output?.kwargs?.content ?? output?.content ?? output ?? null;

  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return raw;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const OmniAgentProvider = ({ children }) => {
  // The app's own dialog rather than window.alert, which Chrome draws with the
  // page URL on it and which blocks the main thread while it is open.
  const notify = useConfirm();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const panelTurn = useRef({ moved: false, clicked: false });
  const [panelCounts, setPanelCounts] = useState({});
  const [jobMatches, setJobMatches] = useState({});
  const choosePanel = useCallback((tab) => {
    panelTurn.current.clicked = true;
    if (tab) setPanelCounts(counts => ({ ...counts, [tab]: 0 }));
  }, []);
  // Rail links, tabs, and Back all express the person's choice. Tool moves
  // call navigate directly and never pass through this click listener.
  useEffect(() => {
    const clicked = e => {
      const link = e.target.closest?.('a[href^="/"], .shell-close');
      if (link) choosePanel(panelAt((link.getAttribute('href') || '').split('?')[0])?.label);
    };
    const back = () => choosePanel();
    document.addEventListener('click', clicked, true);
    window.addEventListener('popstate', back);
    return () => { document.removeEventListener('click', clicked, true); window.removeEventListener('popstate', back); };
  }, [choosePanel]);


  // ── One conversation, on every page ─────────────────────────────────────
  //
  // History: the chat was one conversation across the app, then (Sep 2026)
  // one thread per page, because a page opened onto somebody's unrelated
  // conversation and felt stitched together. That split only ever lived on
  // the screen: the server kept ONE session (activeSessionId below), so the
  // resume page showed part of a conversation whose other half was hidden on
  // another page, and the assistant remembered things the screen did not show.
  //
  // Since 2026-09-25 the assistant is one assistant with every tool on every
  // page (services/omni-agent/agents/assistant.js), and the founder chose one
  // conversation with a list of past chats, the way Claude and ChatGPT work:
  // the conversation follows the person from page to page, "New Chat" starts
  // a fresh one, and "Recent Chats" in the navigation panel switches between
  // them. The screen now shows exactly the session the server holds.
  //
  // The thread map and the setter's signature are kept, so the streaming code
  // below did not change; every page simply maps to the one thread.
  //
  // The page is not read here (2026-10-31): every page is the one thread, and
  // reading the location made the whole chat, and everything reading it,
  // render again on every change of page.
  const [threads, setThreads] = useState({ main: [], resume: [], answers: [], jobs: [] });
  const surface = 'main';
  const surfaceRef = useRef(surface);
  surfaceRef.current = surface;
  // While a reply is streaming, every write targets the surface the message
  // was SENT from — navigating mid-answer must not splice a resume reply into
  // the job-agent thread.
  const originRef = useRef(null);
  const messages = threads[surface] || NO_MESSAGES;
  const setMessages = useCallback((updater) => {
    setThreads((t) => {
      const key = originRef.current || surfaceRef.current;
      const current = t[key] || [];
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...t, [key]: next };
    });
  }, []);
  // The applications this conversation started: their replies ask what only
  // the person can answer for them, and the shell keeps Applications running
  // for them (decision A, 2026-09-28). A reopened conversation carries the
  // same tool results, so it asks again whatever is still unanswered.
  const startedRef = useRef(new Map());
  const started = useMemo(() => {
    const next = startedBy(messages);
    if (!sameStarted(next, startedRef.current)) startedRef.current = next;
    return startedRef.current;
  }, [messages]);
  // Chat overlay state (starts closed so mobile users are never greeted with a blocking modal)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Chat Sessions
  // The chat starts on Auto on every plan (chat-2, 2026-10-30): the server
  // picks the model for each turn. It started on GPT-4o mini from
  // 2026-09-29 so a fresh session never ran on Sonnet by accident, and on
  // 'claude-3-5-sonnet' (Sonnet 5 on the server) before that. The chat sends
  // this id with every turn. A Pro or Max person still picks a model.
  const [activeModel, setActiveModel] = useState(DEFAULT_MODEL);
  // The model menu, as the server gives it for this person (see
  // services/modelMenu.js): Auto alone until it answers. Refreshed after
  // every turn, because a turn changes what is left.
  const [modelMenu, setModelMenu] = useState(FALLBACK_MENU);
  const [usageSummary, setUsageSummary] = useState(null);
  // The meter under the composer (window, week, month; docs/plans/chat-2.md
  // "The meter"). From /api/plans/usage on load and after each turn, and
  // from each turn's `usage` event, so it holds between turns.
  const [meter, setMeter] = useState(null);
  const refreshMenu = useCallback(async () => {
    const { menu, usage } = await fetchModelMenu();
    setModelMenu(menu);
    setUsageSummary(usage);
    if (usage?.meter) setMeter(usage.meter);
  }, []);
  useEffect(() => { if (currentUser) refreshMenu(); }, [currentUser, refreshMenu]);
  // "Think harder" in the picker (Pro and Max): while it is on, every message
  // goes to the deep route. It turns itself off when the day's allowance is
  // used up, so the next message does not quietly run on something else.
  const [thinkHarder, setThinkHarder] = useState(false);
  useEffect(() => { if (thinkHarder && deepLeft(meter) === 0) setThinkHarder(false); }, [thinkHarder, meter]);

  // ── The one saved model (founder, 2026-09-29) ──────────────────────────
  //
  // Account → Assistant saves it; the chat opens on it when the plan allows
  // it and the server can run it, else on DEFAULT_MODEL (modelChoice.js). A
  // pick in the chat's own picker holds for that conversation only: a new
  // conversation, or another one opened, starts on the saved choice again.
  // It used to be memory only, never saved, and never read what Account
  // saved — two pickers, each ignoring the other.
  const [savedModel, setSavedModel] = useState(null);
  const switchedRef = useRef(false);
  useEffect(() => {
    if (!currentUser) { setSavedModel(null); return undefined; }
    let live = true;
    readSavedModel(currentUser.uid).then((id) => { if (live) setSavedModel(id); });
    return () => { live = false; };
  }, [currentUser]);
  // Saved on Account while this chat is open: it follows, unless this
  // conversation was switched by hand.
  useEffect(() => {
    const onSaved = (e) => setSavedModel(e?.detail?.id || null);
    window.addEventListener(MODEL_SAVED, onSaved);
    return () => window.removeEventListener(MODEL_SAVED, onSaved);
  }, []);
  const opening = useMemo(() => openingModel(savedModel, usageSummary), [savedModel, usageSummary]);
  useEffect(() => { if (!switchedRef.current) setActiveModel(opening); }, [opening]);
  // Never an id the server does not list (chat-2): a model picked earlier
  // that the server has since stopped offering here (its provider went down,
  // or the plan changed) gives way to what the chat opens on.
  useEffect(() => {
    if (!Array.isArray(usageSummary?.menu)) return;
    if (activeModel !== DEFAULT_MODEL && !canUseModel(usageSummary.menu, activeModel)) setActiveModel(opening);
  }, [usageSummary, activeModel, opening]);
  // The chat's picker: this conversation only.
  const switchModel = useCallback((id) => { switchedRef.current = true; setActiveModel(id); }, []);
  const openOnSaved = () => { switchedRef.current = false; setActiveModel(opening); };
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  
  // Track active UI edit state without muddying the URL
  const [activeEditContext, setActiveEditContext] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  // What happened to each proposal once decided, by changeId: 'applied' or
  // 'discarded'. The chat's short card reads it after a review in the Resume
  // panel, where the decision was made (2026-09-28).
  const [changeOutcomes, setChangeOutcomes] = useState({});
  const markOutcome = (changes, outcome) => setChangeOutcomes(prev => ({
    ...prev, ...Object.fromEntries(changes.filter(c => c?.changeId).map(c => [c.changeId, outcome])),
  }));
  // The section arrangements the assistant proposed (arrange_resume_sections,
  // chat-2), kept apart from pendingChanges: they are not entry edits, and
  // the Resume panel's review reads pendingChanges row by row. Decided on
  // their own card (ArrangeCard.js); the outcome, by proposalId, is
  // 'applied' or 'discarded'.
  const [sectionProposals, setSectionProposals] = useState([]);
  const [arrangeOutcomes, setArrangeOutcomes] = useState({});

  // Notify backend instantly when model dropdown is changed
  useEffect(() => {
    if (!currentUser) return;
    const notifyBackend = async () => {
      try {
        const idToken = await currentUser.getIdToken();
        await fetch(`${API_BASE}/api/omni-agent/set-model`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ modelId: activeModel })
        });
      } catch (e) {
        console.error("Failed to notify backend of model change", e);
      }
    };
    notifyBackend();
  }, [activeModel, currentUser]);

  // Fetch sessions on load
  const fetchSessions = async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      // The route returns { error: '...' } (not an array) on failure —
      // e.g. a transient DB timeout — and this used to get set as-is,
      // crashing NavigationPanel.js's sessions.map() and taking down the
      // whole app shell (confirmed live 2026-08). Keep whatever sessions
      // we already had rather than replacing them with something unusable.
      if (Array.isArray(data)) setSessions(data);
      else console.error('[OmniAgent] /api/chats did not return an array, keeping existing sessions:', data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchSessions();
  }, [currentUser]);

  const loadSession = async (id) => {
    if (!currentUser) return;
    setActiveSessionId(id);
    setMessages([]);
    openOnSaved();
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/chats/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setJobMatches(Object.fromEntries(data.flatMap(m => (m.toolCalls || []).filter(t => t.panel?.focus?.job && t.panel.focus.match).map(t => [t.panel.focus.job, t.panel.focus.match]))));
        setMessages(data.map(m => ({
          id: m.id,
          role: m.role,
          content: m.content,
          tools: m.toolCalls || [],
          usage: m.usage || null,
          // ChatMessage has recorded this all along and the API returns the
          // whole row; it was being dropped here, so a reopened conversation
          // had no sense of when any of it happened.
          createdAt: m.createdAt,
        })));
      }
    } catch (e) { console.error(e); }
  };

  const startNewSession = () => {
    setActiveSessionId(null);
    setMessages([]);
    openOnSaved();
    try { sessionStorage.removeItem('omni.activeSession'); } catch { /* private mode */ }
  };

  // The conversation survives a reload. It used to start over every time:
  // the Auto-apply page reloaded, opened a fresh chat, and asked the
  // assistant the same things again (founder, 2026-09-24). Per tab, so two
  // tabs are two conversations, and gone when the tab closes.
  useEffect(() => {
    try { if (activeSessionId) sessionStorage.setItem('omni.activeSession', activeSessionId); } catch { /* private mode */ }
  }, [activeSessionId]);
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!currentUser || restoredRef.current) return;
    restoredRef.current = true;
    let saved = null;
    try { saved = sessionStorage.getItem('omni.activeSession'); } catch { saved = null; }
    if (saved) loadSession(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  
  const deleteSession = async (sessionId) => {
    try {
      if (!currentUser) return;
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/chats/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        if (activeSessionId === sessionId) {
          startNewSession();
        }
        fetchSessions();
      }
    } catch (e) {
      console.error("Failed to delete session", e);
    }
  };

  // Shared PATCH helper backing Pin, Rename, and Link-to-workspace — all three
  // hit the same existing /api/chats/:id endpoint, just with a different field.
  const updateSession = async (sessionId, patch) => {
    if (!currentUser) return;
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/chats/${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(patch)
      });
      if (res.ok) {
        fetchSessions();
      } else {
        const body = await res.json().catch(() => ({}));
        console.error('Failed to update session', res.status, body);
        notify({
          title: "Couldn't update this chat",
          body: body.error || 'Please try again.',
          confirmLabel: 'OK', cancelLabel: null, variant: 'danger', icon: 'error',
        });
      }
    } catch (e) {
      console.error("Failed to update session", e);
      notify({
        title: "Couldn't update this chat",
        body: 'Please try again.',
        confirmLabel: 'OK', cancelLabel: null, variant: 'danger', icon: 'error',
      });
    }
  };

  const pinSession = (sessionId, isPinned) => updateSession(sessionId, { isPinned });
  const renameSession = (sessionId, title) => updateSession(sessionId, { title });
  const linkSessionWorkspace = (sessionId, linkedWorkspace) => updateSession(sessionId, { linkedWorkspace });

  const abortControllerRef = useRef(null);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // Stops an in-flight response. Aborting the fetch closes the SSE socket,
  // which the backend detects and uses to halt the graph — so this actually
  // stops the work rather than just hiding it.
  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsProcessing(false);
  };

  /**
   * Asks the last question again, replacing the answer rather than appending a
   * second one.
   *
   * Resending through sendMessage alone would leave the conversation showing
   * the same question twice with two answers under it, and the next turn would
   * carry both into the model's context as though the user had genuinely asked
   * twice. So the previous reply is dropped first.
   *
   * `{ thinkHarder: true }`: the "Think harder" suggestion under a reply
   * (chat-2) asks the same question again on the deep route. Read off an
   * options object only, because the buttons that call this pass their click
   * event.
   */
  const regenerate = async (options) => {
    if (isProcessing) return;
    const deeper = options?.thinkHarder === true;

    // Walk back to the question this reply was answering.
    // Skips the hidden accept/reject notes — resending one would ask the
    // assistant to answer "(I rejected that change.)" as though it were a
    // question.
    const lastUserIdx = messages.map(m => (m.role === 'user' && !m.hidden ? 'user' : 'other')).lastIndexOf('user');
    if (lastUserIdx === -1) return;

    const question = messages[lastUserIdx].content;
    // Drop the question and everything after it; sendMessage re-adds the
    // question itself, so keeping it here would duplicate it.
    setMessages(prev => prev.slice(0, lastUserIdx));
    await sendMessage(question, deeper ? { thinkHarder: true } : {});
  };

  /**
   * A card in the transcript that nothing is sent for: the Auto-apply
   * page's summary of what the server answered. Local to this tab —
   * the record itself is on each run's history.
   */
  // `key`: one card per thing (a job) — posting again replaces it in place
  // instead of stacking a copy under it.
  const postCard = (text, { role = 'ai', key = null } = {}) => {
    const id = key ? `card-${key}` : `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setMessages(prev => {
      const at = prev.findIndex(m => m.id === id);
      const msg = { id, role, content: text, local: true, createdAt: new Date().toISOString() };
      if (at === -1) return [...prev, msg];
      // Moved to the end: the newest pass is what the person just asked for.
      return [...prev.slice(0, at), ...prev.slice(at + 1), msg];
    });
    return id;
  };
  /** Replaces a posted card's text (a pass that started, now finished). */
  const updateCard = (id, text) => {
    setMessages(prev => (prev.some(m => m.id === id)
      ? prev.map(m => (m.id === id ? { ...m, content: text } : m))
      // Gone (a new chat was started): brought back, so the update is seen.
      : [...prev, { id, role: 'ai', content: text, local: true, createdAt: new Date().toISOString() }]));
  };
  /** Takes a posted card away (a pass that had nothing to say). */
  const removeCard = (id) => {
    setMessages(prev => prev.filter(m => m.id !== id));
  };

  // `setup`: a turn from the chat-led setup, free for the person (the server
  // checks its allowance: Node Backend aiUsage freeSetup).
  // `thinkHarder`: this one turn on the deep route (chat-2), from the "Think
  // harder" suggestion; the picker's Think harder switch does the same for
  // every turn while it is on. The server decides what the plan allows.
  const sendMessage = async (text, { setup = false, thinkHarder: deeper = false } = {}) => {
    // Everything this send streams belongs to the surface it started on.
    originRef.current = surfaceRef.current;
    if (!text.trim() || !currentUser) return;

    // Abort previous stream if still running
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const newMsg = { id: Date.now(), role: 'user', content: text, createdAt: new Date().toISOString() };
    setMessages(prev => [...prev, newMsg]);
    panelTurn.current = { moved: false, clicked: false };
    setIsProcessing(true);

    // Create an empty AI message to stream into
    const aiMsgId = Date.now() + 1;
    // Words from the moment Send is pressed, and one clock for the whole
    // turn: "Thinking 0s" at once instead of bare dots, and a time that never
    // restarts between steps (founder, 2026-09-27: "a minute since I am
    // waiting" and nothing said what was happening).
    const turnStartedAt = Date.now();
    setMessages(prev => [...prev, { id: aiMsgId, role: 'ai', content: '', tools: [], createdAt: new Date().toISOString(),
      turnStartedAt, lastEventAt: turnStartedAt, live: { phase: 'thinking', tool: null, label: null, since: turnStartedAt } }]);
    const touch = (extra = {}) => setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, lastEventAt: Date.now(), ...extra } : m)));

    // The line between slow and gone: the server pings every ten seconds, so
    // silence past 45 s after a ping means the connection died, not that the
    // model is thinking. Watched only once a ping has arrived, so an older
    // server without pings never trips it.
    let lastBytes = Date.now();
    let sawPing = false;
    const watchdog = setInterval(() => {
      if (!sawPing || Date.now() - lastBytes < 45_000) return;
      clearInterval(watchdog);
      setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, lost: true, live: null } : m)));
      if (abortControllerRef.current) abortControllerRef.current.abort();
    }, 1000);

    // Marks the assistant bubble as failed with a message the user can act on.
    // Previously a non-200 response threw on `response.body.getReader()` and was
    // swallowed by the catch, leaving a permanently blank bubble with no
    // explanation and no way to tell it apart from a slow response.
    const failBubble = (msg) => {
      setMessages(prev => prev.map(m =>
        m.id === aiMsgId ? { ...m, content: m.content || msg, isError: true, live: null } : m
      ));
    };

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE}/api/omni-agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          message: text,
          ...(setup ? { setup: true } : {}),
          conversationHistory: messages.map(m => ({ role: m.role, content: m.content })),
          // Sent separately because the server rebuilds the conversation from
          // the database and only falls back to conversationHistory if that
          // fails. These notes exist only in the browser — nothing persists
          // them — so inside conversationHistory they were silently dropped,
          // and the assistant kept describing a rejected change as still
          // waiting to be accepted.
          outcomes: messages.filter(m => m.hidden).map(m => m.content),
          pageContext: (() => {
            const params = new URLSearchParams(window.location.search);
            if (activeEditContext) {
              params.set('edit', activeEditContext.id);
              if (activeEditContext.version) params.set('version', activeEditContext.version);
            }
            return window.location.pathname + (params.toString() ? `?${params}` : '');
          })(),
          // "auto" unless the person picked a model the server listed.
          modelId: activeModel || DEFAULT_MODEL,
          ...(deeper || thinkHarder ? { thinkHarder: true } : {}),
          // So "back at 3:10 pm" is said in the person's own time.
          ...(browserTimeZone() ? { timeZone: browserTimeZone() } : {}),
          sessionId: activeSessionId
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok || !response.body) {
        // A 429 from the meter says what happened and when the budget resets.
        const limitBody = response.status === 429 ? await response.clone().json().catch(() => null) : null;
        failBubble(
          response.status === 401
            ? "Your session expired. Refresh the page and sign in again."
            : response.status === 429
              ? (limitBody?.message || "Too many requests right now. Wait a moment and try again.")
              : "I couldn't reach the server. Check your connection and try again."
        );
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        lastBytes = Date.now();
        const piece = decoder.decode(value, { stream: true });
        if (piece.includes(': ping')) sawPing = true;
        buffer += piece;
        
        // Parse SSE
        const parts = buffer.split('\n\n');
        buffer = parts.pop(); // keep incomplete chunk
        
        for (const part of parts) {
          if (part.startsWith('data: ')) {
            const dataStr = part.replace('data: ', '');
            try {
              const data = JSON.parse(dataStr);
              if (data.type === 'session_created') {
                setActiveSessionId(data.sessionId);
                fetchSessions(); // Refresh list to show the new chat
              } else if (data.type === 'status') {
                // The one quiet line while the turn works (chat-2): each
                // status REPLACES the last, in the server's words when it
                // sends them ("Searching 889,000 jobs…"). Shown with the
                // turn's clock until the reply's first words arrive.
                const label = typeof data.label === 'string' && data.label.trim() ? data.label.trim() : null;
                setMessages(prev => prev.map(m => (m.id === aiMsgId
                  ? { ...m, lastEventAt: Date.now(), live: (m.live && m.live.phase === data.phase && m.live.tool === (data.tool || null) && m.live.label === label) ? m.live : { phase: data.phase, tool: data.tool || null, label, since: Date.now() } }
                  : m)));
              } else if (data.type === 'token') {
                setMessages(prev => prev.map(m =>
                  m.id === aiMsgId ? { ...m, content: m.content + data.content, live: null, lastEventAt: Date.now() } : m
                ));
              } else if (data.type === 'route') {
                // Which model answers this turn and why, shown under the
                // finished reply ("Auto · Claude Sonnet 5.5").
                const { type, ...route } = data;
                setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, route } : m)));
              } else if (data.type === 'notice') {
                // One plain line above the reply ("Using the faster model
                // until 3:10 pm."). Several stack; the same one is said once.
                if (data.message) {
                  setMessages(prev => prev.map(m => (m.id === aiMsgId && !(m.notices || []).some(n => n.message === data.message)
                    ? { ...m, lastEventAt: Date.now(), notices: [...(m.notices || []), { kind: data.kind || null, message: String(data.message) }] }
                    : m)));
                }
              } else if (data.type === 'suggestions') {
                // Two or three next steps, as chips under the finished reply.
                const items = (Array.isArray(data.items) ? data.items : [])
                  .filter(s => s && typeof s.label === 'string' && s.label.trim() && (typeof s.prompt === 'string' || s.kind === 'think_harder'))
                  .slice(0, 3)
                  .map(s => ({ label: s.label.trim(), prompt: s.prompt || '', kind: s.kind || null }));
                setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, suggestions: items } : m)));
              } else if (data.type === 'tool_start') {
                // `input` was being discarded here, so tool.input.uiMessage was
                // always undefined and every chip fell through to a generic
                // label — the specific one-line description the model is asked
                // to write for each call ("Rewriting Acme bullets") was
                // computed, streamed, and then thrown away.
                //
                // startedAt drives the elapsed-time readout: a step that takes
                // fifteen seconds with no indication of progress is
                // indistinguishable from one that has hung.
                //
                // The steps are for the cards and for "Worked for 38s" once
                // the turn is done; while it works, the status line says
                // what is happening (chat-2). A step keeps the server's
                // words on that line, or names itself when there are none
                // (a server from before chat-2 sent no words).
                const stepLabel = typeof data.label === 'string' && data.label.trim() ? data.label.trim() : null;
                setMessages(prev => prev.map(m =>
                  m.id === aiMsgId
                    ? {
                      ...m,
                      live: stepLabel || !m.live?.label
                        ? { phase: 'working', tool: data.name, label: stepLabel, since: Date.now() }
                        : m.live,
                      lastEventAt: Date.now(),
                      tools: [...(m.tools || []), { name: data.name, kind: data.kind, status: 'running', input: data.input, label: stepLabel || undefined, startedAt: Date.now() }],
                    }
                    : m
                ));
              } else if (data.type === 'tool_end') {
                touch();
                const decision = followPanel(panelTurn.current, data.panel, data.kind, { phone: window.innerWidth <= 768 });
                panelTurn.current = decision.turn;
                if (decision.move) {
                  navigate(panelUrl(data.panel));
                  setPanelCounts(counts => ({ ...counts, [data.panel.tab]: 0 }));
                } else if (decision.count) {
                  setPanelCounts(counts => ({ ...counts, [data.panel.tab]: (counts[data.panel.tab] || 0) + 1 }));
                }
                // An answer or a story the chat saved: About you and the Get
                // started bar read again, beside the chat, as it happens.
                if (['save_answer', 'confirm_answer', 'save_story_draft'].includes(data.name)) factsChanged('chat');
                if (data.name === 'update_design_settings') {
                  const parsed = parseToolOutput(data.output);
                  if (parsed?.action === 'UPDATE_DESIGN') {
                    window.dispatchEvent(new CustomEvent('design-studio-saved', { detail: parsed.payload }));
                    window.dispatchEvent(new CustomEvent('refresh-profile'));
                  }
                } else if (['update_resume_entry', 'add_resume_entry', 'delete_resume_entry', 'batch_update_resume'].includes(data.name)) {
                  try {
                    const parsed = parseToolOutput(data.output);

                    if (parsed && parsed.status === 'PROPOSED_CHANGE') {
                      // Attach the tool's message ID so we can render it inline,
                      // plus a stable changeId — accept/reject used to filter by
                      // object identity, which breaks the moment a change object
                      // is copied or re-created.
                      setPendingChanges(prev => [...prev, {
                        ...parsed,
                        messageId: aiMsgId,
                        changeId: `${aiMsgId}-${prev.length}`
                      }]);

                    } else if (parsed && parsed.status === 'SUCCESS') {
                      window.dispatchEvent(new CustomEvent('refresh-profile'));
                    }
                  } catch (e) { console.error("Error parsing tool output", e, data.output); }
                } else if (data.name === 'create_resume_variant') {
                  // invalidateResumes, not a hand-dispatched 'resumes-changed':
                  // the event alone made every listener re-read the list from
                  // the still-valid five-minute cache, so the switcher never
                  // showed the copy the AI had just made and the feed kept the
                  // old resume's scores. The service drops the cache and fires
                  // the same event (2026-09-04).
                  try {
                    const parsed = parseToolOutput(data.output);
                    const newResumeId = parsed?.resumeId;
                    invalidateResumes();
                    if (newResumeId) {
                      window.dispatchEvent(new CustomEvent('active-resume-changed', { detail: { resumeId: newResumeId } }));
                    }
                    window.dispatchEvent(new CustomEvent('refresh-profile'));
                  } catch (e) {
                    invalidateResumes();
                    window.dispatchEvent(new CustomEvent('refresh-profile'));
                  }
                } else if (data.name === 'arrange_resume_sections') {
                  // A new order, hidden or shown sections, new titles: a
                  // proposal the person decides on its own card.
                  const parsed = parseToolOutput(data.output);
                  if (parsed && parsed.status === 'PROPOSED_CHANGE' && parsed.action === 'ARRANGE_SECTIONS' && parsed.settings) {
                    setSectionProposals(prev => [...prev, {
                      ...parsed,
                      messageId: aiMsgId,
                      proposalId: `${aiMsgId}-sections-${prev.length}`,
                    }]);
                  }
                } else if (data.name === 'compute_job_match') {
                  try {
                    const parsed = parseToolOutput(data.output);
                    if (parsed && parsed.score !== undefined) {
                      if (parsed.jobId) setJobMatches(matches => ({ ...matches, [parsed.jobId]: parsed }));
                    }
                  } catch (e) { /* ignore non-json */ }
                }
                // The branch that used to sit here listened for
                // 'update_resume_section', 'reorder_sections' and
                // 'toggle_section'. No such tools exist — section arrangement
                // has no tool at all — so it was dead code that made the
                // capability look implemented from the frontend side.

                // A tool that returned an error must not render as a completed
                // step. Every tool chip was marked 'done' regardless of
                // outcome, so a failed call showed the same green check as a
                // successful one and the only trace was whatever the model
                // chose to say about it.
                let toolFailed = false;
                try {
                  const out = typeof data.output === 'string' ? JSON.parse(data.output) : data.output;
                  toolFailed = out?.status === 'ERROR';
                } catch { /* non-JSON output is not an error signal */ }

                // The first still-running step of that name: two saves in one
                // turn are two steps, and the second's result is not the first's.
                // verified/checked come from the server's work loop: a change
                // is marked checked only when it was read back and landed.
                setMessages(prev => prev.map(m => {
                  if (m.id !== aiMsgId) return m;
                  let hit = false;
                  return {
                    ...m,
                    tools: (m.tools || []).map(t => {
                      if (hit || t.name !== data.name || t.status !== 'running') return t;
                      hit = true;
                      return {
                        ...t,
                        status: data.refused ? 'refused' : toolFailed ? 'failed' : 'done',
                        output: data.output,
                        panel: data.panel,
                        verified: data.verified,
                        checked: data.checked,
                        elapsedMs: t.startedAt ? Date.now() - t.startedAt : undefined,
                      };
                    }),
                  };
                }));
              } else if (data.type === 'error') {
                // Backend hit an exception mid-stream (LLM API failure, DB
                // error, etc.) and ended the response without a 'done'
                // event. Surface it instead of leaving the AI bubble blank.
                //
                // The server sends the real error as `detail` alongside the
                // friendly line, and this used to drop it on the floor. That
                // left "Something went wrong on my end" as the only evidence
                // anywhere outside the server log, so a user reporting a
                // failure could say nothing more useful than that it failed.
                // The reader still sees the friendly line; the cause is one
                // console open away.
                if (data.detail) {
                  console.error('[assistant] turn failed:', data.detail);
                }
                failBubble(data.message || "Something went wrong on my end. Please try again.");
              } else if (data.type === 'usage') {
                // The receipt for this reply: model, tokens, share of the
                // month. It now follows 'done' by a beat, so it is attached
                // to this turn's own bubble, and used to move the month
                // strip without a round trip.
                const { type, meter: turnMeter, ...receipt } = data;
                setMessages(prev => prev.map(m => (m.id === aiMsgId ? { ...m, usage: receipt } : m)));
                if (typeof receipt.monthPct === 'number') {
                  setUsageSummary(s => (s ? { ...s, pct: receipt.monthPct } : s));
                }
                // The meter after this turn, without waiting for the menu.
                if (turnMeter && typeof turnMeter === 'object') setMeter(turnMeter);
              } else if (data.type === 'done') {
                // How long the whole turn took, shown under it ("Worked for
                // 38s"). An older server's next-actions block is taken out of
                // the text kept, so a copy or the next turn never carries it.
                setMessages(prev => prev.map(m => (m.id === aiMsgId
                  ? { ...m, content: stripNext(m.content), live: null, workedMs: m.turnStartedAt ? Date.now() - m.turnStartedAt : null }
                  : m)));
                setIsProcessing(false);
              }
            } catch (e) {
              console.error("Error parsing SSE:", e, dataStr);
            }
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error("Chat error:", e);
        failBubble("I couldn't reach the server. Check your connection and try again.");
      }
    } finally {
      clearInterval(watchdog);
      // A turn changes 'turns left'; reprice the menu.
      refreshMenu();
      originRef.current = null;
      // Safety net: whatever caused the stream to end — a clean 'done'
      // event, a backend 'error' event, or the connection just closing
      // without either — isProcessing must never stay stuck true, or the
      // send button locks up until the page is reloaded.
      setIsProcessing(false);
    }
  };

  // Returns true/false so callers (DiffCard) can show an accurate
  // success/failure state instead of assuming the save always worked —
  // fetch() only rejects on network failure, not on a 4xx/5xx response, so
  // this must check res.ok explicitly or a failed save would silently look
  // identical to a successful one in the UI.
  const acceptChange = async (change) => {
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/profile/apply-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify(change)
      });
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);

      // Say what changed and where, so the editor can point at it. Accepting a
      // change used to refresh the panes silently: the text on the right was
      // different, but nothing drew the eye to it, so people pressed Accept and
      // could not tell whether anything had happened.
      window.dispatchEvent(new CustomEvent('refresh-profile', {
        detail: { type: change.type, action: change.action },
      }));
      window.dispatchEvent(new CustomEvent('resume-change-applied', {
        detail: { type: change.type, action: change.action, entryId: change.entryId },
      }));
      setPendingChanges(prev => prev.filter(c => c.changeId !== change.changeId));
      markOutcome([change], 'applied');
      return true;
    } catch (err) {
      console.error("Error applying change:", err);
      return false;
    }
  };

  // Applies a whole turn's worth of proposals in order. Sequential rather than
  // parallel because each write snapshots the resume first — firing them
  // concurrently would interleave the snapshots and produce a version history
  // the user can't reason about. Returns true only if every change landed.
  // `note` is the card's own account when the person kept only some of the
  // changes or edited them (DiffCard's outcomeNote): the assistant has to know
  // which lines are NOT on the resume, or it reports them as applied.
  const acceptChanges = async (changes, { note = null } = {}) => {
    let allOk = true;
    for (const change of changes) {
      const ok = await acceptChange(change);
      if (!ok) allOk = false;
    }
    // Only claimed when every write actually landed. Telling the model an edit
    // is saved when it failed is the same lie in the other direction.
    noteOutcome(allOk
      ? (note || `(I accepted ${changes.length > 1 ? 'those changes' : 'that change'}. They are now saved to my resume.)`)
      : `(I tried to accept ${changes.length > 1 ? 'those changes' : 'that change'} but the save failed. Nothing was written.)`
    );
    return allOk;
  };

  // Proposals the person left wholly unticked while applying others: cleared
  // from the card without a note of their own — the accept note names them.
  const dismissChanges = (changes) => {
    const ids = new Set(changes.map(c => c.changeId));
    setPendingChanges(prev => prev.filter(c => !ids.has(c.changeId)));
    markOutcome(changes, 'discarded');
  };

  const rejectChange = (change) => {
    setPendingChanges(prev => prev.filter(c => c.changeId !== change.changeId));
  };

  /**
   * Tells the assistant what the user did with a proposal.
   *
   * Accepting wrote to the database and rejecting cleared the card, but
   * neither said anything to the model. On the next question it looked back at
   * its own PROPOSED_CHANGE in the history, saw no outcome recorded, and
   * assumed the card was still waiting — so a rejected edit was reported as
   * "still sitting in the card above waiting for you to accept it".
   *
   * The note is a user message because that is the only role the server maps
   * to a human turn, and because it is true: the user is the one who decided.
   * `hidden` keeps it out of the transcript on screen.
   */
  const noteOutcome = (text) => {
    setMessages(prev => [...prev, {
      id: `note-${Date.now()}`,
      role: 'user',
      content: text,
      hidden: true,
      createdAt: new Date().toISOString(),
    }]);
  };

  const rejectChanges = (changes) => {
    const ids = new Set(changes.map(c => c.changeId));
    setPendingChanges(prev => prev.filter(c => !ids.has(c.changeId)));
    markOutcome(changes, 'discarded');
    // "Rejected" normally reads as "try again", and the assistant took it that
    // way — it answered the next question correctly and then proposed a fresh
    // version unasked, so the user got another card to read for a request they
    // had already turned down. Rejecting is a full stop, not a retry.
    noteOutcome(
      `(I rejected ${changes.length > 1 ? 'those proposed changes' : 'that proposed change'}. ` +
      `Nothing was saved and the card is gone. Do not describe it as still waiting to be accepted, ` +
      `and do not write another version of it. I will ask again if I want one. ` +
      `Just acknowledge it briefly and wait.)`
    );
  };

  /**
   * Applies a proposed arrangement of the resume's sections (ArrangeCard).
   * Writes exactly what the Resume editor writes when the person arranges
   * sections by hand (ProfileContainer.js savePreviewSetting): PUT
   * /api/profile/sectionSettings { data }, kept alive through a reload. Then
   * the same events a design change raises, so the studio and the PDF read
   * the resume again. Returns true only when the server took it.
   */
  const acceptArrangement = async (proposal) => {
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/profile/sectionSettings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: proposal.settings }),
        keepalive: true,
      });
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
      window.dispatchEvent(new CustomEvent('design-studio-saved', { detail: { sectionSettings: proposal.settings } }));
      window.dispatchEvent(new CustomEvent('refresh-profile', { detail: { type: 'SECTIONS', action: 'ARRANGE' } }));
      setArrangeOutcomes(prev => ({ ...prev, [proposal.proposalId]: 'applied' }));
      noteOutcome('(I accepted the section changes. They are now saved to my resume.)');
      return true;
    } catch (err) {
      console.error('Error arranging sections:', err);
      return false;
    }
  };

  // Nothing is written; the assistant is told, the same way a rejected edit is.
  const rejectArrangement = (proposal) => {
    setArrangeOutcomes(prev => ({ ...prev, [proposal.proposalId]: 'discarded' }));
    noteOutcome(
      '(I rejected the proposed section changes. Nothing was saved. ' +
      'Do not propose the same arrangement again unless I ask. Just acknowledge it briefly and wait.)'
    );
  };

  // The latest code behind every action, and the stable functions that call it.
  const impl = useRef(null);
  impl.current = {
    sendMessage, postCard, updateCard, removeCard, regenerate, toggleSidebar,
    // The chat's picker sets the model for this conversation only.
    setActiveModel: switchModel,
    setThinkHarder, refreshMenu, loadSession, startNewSession, deleteSession, pinSession,
    renameSession, linkSessionWorkspace, setActiveEditContext, acceptChange, acceptChanges,
    dismissChanges, rejectChange, rejectChanges, acceptArrangement, rejectArrangement,
    stopGeneration, setIsSidebarOpen, choosePanel,
  };
  const actions = useMemo(
    () => Object.freeze(Object.fromEntries(ACTIONS.map(name => [name, (...args) => impl.current[name](...args)]))),
    [],
  );

  const value = useMemo(() => ({
    messages,
    panelCounts,
    jobMatches,
    isSidebarOpen,
    isProcessing,
    activeModel,
    modelMenu,
    usageSummary,
    meter,
    thinkHarder,
    sessions,
    activeSessionId,
    activeEditContext,
    pendingChanges,
    changeOutcomes,
    sectionProposals,
    arrangeOutcomes,
    ...actions,
  }), [messages, panelCounts, jobMatches, isSidebarOpen, isProcessing, activeModel, modelMenu, usageSummary,
    meter, thinkHarder, sessions, activeSessionId, activeEditContext, pendingChanges, changeOutcomes,
    sectionProposals, arrangeOutcomes, actions]);

  // The store useOmniAgentPick reads: written as this renders, so a child
  // rendering in the same pass reads the same value, and told after the
  // commit, so a picker whose values changed renders once more.
  const storeRef = useRef(null);
  if (!storeRef.current) storeRef.current = makeStore();
  storeRef.current.set(value);
  useLayoutEffect(() => { storeRef.current.emit(); }, [value]);

  const panelResults = useMemo(
    () => ({ panelCounts, jobMatches, choosePanel, startedBy: started }),
    [panelCounts, jobMatches, choosePanel, started],
  );

  return (
    <OmniActionsContext.Provider value={actions}>
      <OmniStoreContext.Provider value={storeRef.current}>
        <OmniAgentContext.Provider value={value}>
          <PanelResultsContext.Provider value={panelResults}>{children}</PanelResultsContext.Provider>
        </OmniAgentContext.Provider>
      </OmniStoreContext.Provider>
    </OmniActionsContext.Provider>
  );
};
